// @ts-nocheck
// Ported byte-identically from v1 views/dashboard/Realtime/list.js.
// Chrome swaps:
//   - v1 PrimeReact <DataTable>/<Column> â†’ obd3-gui @/components/data-table
//     (TanStack). All 8 columns preserved with the same headers, cell bodies,
//     row click handler (opens the User Info modal), and page size (10).
//   - v1 PrimeReact <Dropdown> â†’ react-select (queue/break selectors).
//   - v1 custom.css side-effect import dropped; nothing else in v2 consumes it.
// v1 quirks preserved verbatim:
//   - Two duplicate EventSource flows: `fetchRealtimeData` (declared but only
//     called after break add/remove for a stale re-fetch) plus the primary
//     useEffect. Both open a new SSE and only the second one is cleaned up.
//   - Buttons in the Add Break modal footer label the primary action "Remove"
//     while its text child is "Add".
//   - Commented-out Set/Remove Break buttons kept.
//   - Unused state: statusList, toastProps, showToast, setSelectedAgentName
//     (still gets set on Queue click).
//   - `setAssignedQueueList(...grouped)` spread-arg call in the Queue button
//     onClick â€” likely a bug in v1 (spreads chunked-array back to the setter),
//     but preserved as authored.
import { queueWaitSeconds } from '@/utilities/queueWait';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { DataTable } from '@/components/data-table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import Loader from '@/components/partials/components/Loader';
import { useNavigate } from 'react-router-dom';
import {
  BACKEND_API_BASE_URL,
  USER_ROLES,
  CLIENT_SOURCE,
  ROUTES,
  MODULE_ACL,
} from '@/utilities/constant';
import { getUser, getUserAccessToken, Storage } from '@/utilities/setting';
import Select from 'react-select';

import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import {
  API_USER_LOGOUT_FROM,
  CALL_GROUP_REMOVE_AGENT,
  CALL_GROUP_ADD_AGENT,
  API_BREAK_LIST,
  API_AGENT_BREAK_ADD,
  API_AGENT_BULK_LOGOUT,
} from '@/service/url';
import Boxes from './components/Boxes';
import AgentActivityModal from '@/features/agent-activity/AgentActivityModal';
import SipTraceModal from '@/features/sip-trace/SipTraceModal';
import { motion } from 'framer-motion';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';
import {
  Headphones,
  MessageCircle,
  Users,
  LogOut,
  Pause,
  Play,
  UserPlus,
  UserMinus,
  ListChecks,
  Search,
  Clock,
  LogIn,
  Maximize2,
  Minimize2,
  RefreshCw,
  Download,
  Activity,
  Radio,
  PhoneCall,
  Coffee,
  PowerOff,
  AlertTriangle,
  Smartphone,
  Headset,
  Loader2,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import BreakStatusCapsule from '@/components/BreakStatusCapsule';
import { FillPage } from '@/components/layout/PageContainer';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';

const REALTIME_ROLES = [USER_ROLES.SUPERADMIN, USER_ROLES.CALL_CENTRE_ADMIN];

const isDashboardLoggedIn = (rowData: any) =>
  Boolean(rowData?.user?.login_at) && !rowData?.user?.logout_at;
const isProfileLoggedIn = (rowData: any) => rowData?.agent?.login_status == '1';

const RealtimeList = () => {
  const navigate = useNavigate();
  const realtimeAcl = useModuleAcl(MODULE_ACL.REALTIME);
  const isRealtimeRole = REALTIME_ROLES.includes(getUser()?.role);
  // WebRTC trace is a support tool: superadmin only.
  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const [isDarkMode, setIsDarkMode] = useState(
    typeof document !== 'undefined' && document.body.classList.contains('dark')
  );
  const [statusList, setStatusList] = useState<any>({});
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [realtimeData, setRealTimeData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgentData, setSelectedAgentData] = useState<any>(null);
  const [agentPopupVisible, setAgentPopupVisible] = useState(false);
  const [extension, setExtension] = useState<any>(null);
  const [assignQueueModelView, setAssignQueueModelView] = useState(false);
  const [assignedQueueList, setAssignedQueueList] = useState<any[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<any>([]);
  const [removeQueueModelView, setRemoveQueueModelView] = useState(false);
  const [queueModel, setQueueModel] = useState(false);
  const [queueList, setQueueList] = useState<any[]>([]);
  const [selectedRemoveQueue, setSelectedRemoveQueue] = useState<any>([]);
  const [wholeDay, setWholeDay] = useState(true);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [selectedAgentName, setSelectedAgentName] = useState('');
  const [memberWeekDays, setmemberWeekDays] = useState<string[]>([
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ]);
  const [queueDetail, setQueueDetail] = useState<any>([]);
  const [queuePopupVisible, setQueuePopupVisible] = useState(false);
  const [popupPosition, setPopupPosition] = useState({
    x: window.innerWidth / 2 + 45,
    y: 350,
  });
  const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));
  const [loggedInUser, setLoggedInUserId] = useState('');
  const [breakList, setBreakList] = useState<any[]>([]);
  const [selectedBreak, setSelectedBreak] = useState<any>('');
  const [breakModelVisible, setBreakModelVisible] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activityAgent, setActivityAgent] = useState<any>(null);
  const [sipTraceAgent, setSipTraceAgent] = useState<any>(null);
  const [stateFilter, setStateFilter] = useState<
    'all' | 'oncall' | 'online' | 'break' | 'offline'
  >('all');
  const [lastUpdatedTs, setLastUpdatedTs] = useState<number>(Math.floor(Date.now() / 1000));
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [wallboard, setWallboard] = useState(false);
  const [pendingTypeLogin, setPendingTypeLogin] = useState<{
    agentId: any;
    agentName: string;
    type: string;
    isActive: boolean;
  } | null>(null);
  const [pendingLogout, setPendingLogout] = useState<any | null>(null);
  const [typeLoginBusy, setTypeLoginBusy] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const body = document.body;
    const updateTheme = () => setIsDarkMode(body.classList.contains('dark'));
    const observer = new MutationObserver(updateTheme);

    updateTheme();
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    async function loggedInUser() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User not found in Storage');
        return null;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in user from Storage', e);
        return null;
      }
      setLoggedInUserId(user.role);
    }

    loggedInUser();
  }, []);

  useEffect(() => {
    getApiCall(API_BREAK_LIST)
      .then((data: any) => {
        const options = data.map((dataOpt: any) => ({
          label: dataOpt.title,
          value: dataOpt.id,
        }));

        setBreakList(options);
      })
      .catch((error: any) => {
        console.error('Error fetching Breaks:', error);
      });
  }, []);

  useEffect(() => {
    localStorage.setItem('queuePopupPosition', JSON.stringify(popupPosition));
  }, [popupPosition]);

  useEffect(() => {
    const savedPosition = JSON.parse(
      localStorage.getItem('queuePopupPosition') as any
    );
    if (savedPosition) {
      setPopupPosition(savedPosition);
    }
  }, []);

  const fetchRealtimeData = useCallback(() => {
    const token = getUserAccessToken();
    const es = new EventSource(
      `${BACKEND_API_BASE_URL}/v2/agents/real-time?token=${token}&x-client-source=${CLIENT_SOURCE}`
    );
    let firstMessage = true;

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const arrayData = Array.isArray(data.data) ? data.data : [data.data];
      setRealTimeData(arrayData[0].agents);
      setQueueDetail(arrayData[0].queues);

      if (firstMessage) {
        setLoading(false);
        firstMessage = false;
      }
    };

    es.onerror = () => {
      es.close();
      setLoading(false);
    };

    return es;
  }, []);

  useEffect(() => {
    setLoading(true);
    let eventSource: EventSource;
    const token = getUserAccessToken();

    // getUserAccessToken().then(token => {
    eventSource = new EventSource(
      `${BACKEND_API_BASE_URL}/v2/agents/real-time?token=${token}&x-client-source=${CLIENT_SOURCE}`
    );
    let firstMessage = true;

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const arr = Array.isArray(data.data) ? data.data : [data.data];
      setRealTimeData(arr[0].agents);
      setQueueDetail(arr[0].queues);
      if (firstMessage) {
        setLoading(false);
        firstMessage = false;
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
      setLoading(false);
    };
    // });

    return () => {
      if (eventSource) eventSource.close();
    };
  }, []);

  // useEffect(() => {
  //   if (toastProps) {
  //     setShowToast(true);
  //   }
  // }, [toastProps]);

  useEffect(() => {
    setLastUpdatedTs(Math.floor(Date.now() / 1000));
  }, [realtimeData]);

  const stateOf = (item: any): 'oncall' | 'break' | 'online' | 'offline' => {
    const onCall = item?.agent?.call_status == '1';
    const onBreak = Boolean(item?.user?.break_id || item?.agent?.break_id);
    const online = item?.agent?.login_status == '1';
    if (onCall) return 'oncall';
    if (onBreak) return 'break';
    if (online) return 'online';
    return 'offline';
  };

  const stateCounts = useMemo(() => {
    const c = { oncall: 0, break: 0, online: 0, offline: 0 };
    for (const item of realtimeData) c[stateOf(item)] += 1;
    return c;
  }, [realtimeData]);

  const filteredData = useMemo(() => {
    let data = realtimeData;
    if (stateFilter !== 'all') {
      data = data.filter((item: any) => stateOf(item) === stateFilter);
    }
    if (!searchText) return data;
    const lower = searchText.toLowerCase();
    return data.filter((item: any) => {
      const name = item?.agent?.agent_name?.toLowerCase() || '';
      const profiles = item?.agent?.agentTelephony?.profile || [];
      const activeProfile = profiles.find((p: any) => p.active_profile === true);
      const number = activeProfile?.number?.number?.toString() || '';
      return name.includes(lower) || number.includes(searchText);
    });
  }, [realtimeData, searchText, stateFilter]);

  if (loading) {
    return <Loader />;
  }

  const handleRowClick = (rowData: any) => {
    setAssignedQueueList(rowData?.noAssignedGroup);
    setQueueList(rowData?.agentGroup);

    const profiles = rowData?.agent?.agentTelephony?.profile || [];
    const activeProfile = profiles.find((p: any) => p.active_profile === true);
    const extensionNumber = activeProfile?.number?.number || '-';
    setExtension(extensionNumber);
    setSelectedAgentData(rowData);
    setAgentPopupVisible(true);
  };

  const handleLogoutAgent = (selectedAgentData: any) => {
    if (!selectedAgentData?.agent?.id) {
      toast.error('Agent Id Not Found To Process');
      return;
    }
    postApiCall(`${API_AGENT_BULK_LOGOUT}`, {
      agentIds: [selectedAgentData?.agent?.id],
    })
      .then((resp: any) => {
        toast.success('Agent Logout Successfully');
        setAgentPopupVisible(false);
      })
      .catch((error: any) => {
        toast.error(friendlyApiError(error) || 'Failed to log the agent out');
        setAgentPopupVisible(false);
      });
  };

  const confirmTypeLogin = async () => {
    if (!pendingTypeLogin) return;
    const { agentId, type: typeArg, agentName } = pendingTypeLogin;
    try {
      setTypeLoginBusy(true);
      // toggleStatus API disabled — unguarded licence writer, races with logoutUser/logoutAs/reaper causing licence count drift.
      // await postApiCall(`/v2/agents/status/${agentId}`, {
      //   type: typeArg,
      //   login_status: '1',
      // });
      toast.success(`${agentName} logged in as ${typeArg}`);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Failed to log in as ${typeArg}`);
    } finally {
      setTypeLoginBusy(false);
      setPendingTypeLogin(null);
    }
  };

  const confirmDirectLogout = async () => {
    if (!pendingLogout) return;
    const agentId = pendingLogout?.agent?.id;
    const agentName = pendingLogout?.agent?.agent_name || 'agent';
    if (!agentId) {
      toast.error('Agent ID not found');
      setPendingLogout(null);
      return;
    }
    try {
      setLogoutBusy(true);
      await postApiCall(`${API_AGENT_BULK_LOGOUT}`, { agentIds: [agentId] });
      toast.success(`${agentName} logged out`);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to log the agent out');
    } finally {
      setLogoutBusy(false);
      setPendingLogout(null);
    }
  };

  const handleRemoveQueueClick = () => {
    setRemoveQueueModelView(true);
  };

  const handleRemoveQueue = () => {
    const params = {
      agent_id: selectedAgentData?.agent?.id,
    };
    postApiCall(`${CALL_GROUP_REMOVE_AGENT}/${selectedRemoveQueue}`, params)
      .then((resp: any) => {
        toast.success('Agent Removed Successfully');
        setRemoveQueueModelView(false);
        setSelectedRemoveQueue('');
        setAgentPopupVisible(false);
      })
      .catch((error: any) => {
        toast.error(friendlyApiError(error) || 'Failed to remove the agent from call group');
        setRemoveQueueModelView(false);
      });
  };

  const handleWholeDayChange = (e: any) => {
    const isChecked = e.target.checked;
    setWholeDay(isChecked);

    if (isChecked) {
      setStartTime('00:00');
      setEndTime('23:59');
    } else {
      setStartTime('');
      setEndTime('');
    }
  };

  const handleStartTimeChange = (e: any) => {
    if (!wholeDay) {
      setStartTime(e.target.value);
    }
  };

  const handleEndTimeChange = (e: any) => {
    if (!wholeDay) {
      setEndTime(e.target.value);
    }
  };

  const toMinutes = (timeValue: any) => {
    if (!timeValue || !String(timeValue).includes(':')) return NaN;
    const [hours, minutes] = String(timeValue).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  };

  const handleAddQueueClick = () => {
    setAssignQueueModelView(true);
  };

  const handleAddBreakClick = () => {
    setBreakModelVisible(true);
  };

  const handleAddQueue = () => {
    if (!wholeDay) {
      if (!startTime || !endTime) {
        toast.warning('Please select both start time and end time');
        return;
      }

      const startMins = toMinutes(startTime);
      const endMins = toMinutes(endTime);

      if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
        toast.warning('Invalid time selected');
        return;
      }

      if (memberWeekDays.length === 1 && endMins <= startMins) {
        toast.warning('For a single selected day, end time should be greater than start time');
        return;
      }
    }

    const params = {
      agent_id: [selectedAgentData?.agent?.id],
      type: 'add_member',
      memberSchedulePayload: {
        week_days: memberWeekDays,
        start_time: startTime,
        end_time: endTime,
      },
    };

    postApiCall(`${CALL_GROUP_ADD_AGENT}/${selectedQueue}`, params)
      .then((resp: any) => {
        toast.success('Agent Added Successfully');
        setAssignQueueModelView(false);
        setSelectedQueue('');
        setAgentPopupVisible(false);
      })
      .catch((error: any) => {
        toast.error(friendlyApiError(error) || 'Failed to add the agent to call group');
        setAssignQueueModelView(false);
      });
  };

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h > 0 ? String(h).padStart(2, '0') + ':' : ''}${String(m).padStart(
      2,
      '0'
    )}:${String(s).padStart(2, '0')}`;
  };

  const handleAddBreak = () => {
    const params = {
      break_id: selectedBreak,
      break: 'Added',
    };

    postApiCall(`${API_AGENT_BREAK_ADD}/${selectedAgentData?.agent?.id}`, params)
      .then(async (resp: any) => {
        toast.success(`Break Added Successfully`);
        setBreakModelVisible(false);
        setSelectedBreak('');
        setAgentPopupVisible(false);
        await fetchRealtimeData();
      })
      .catch((error: any) => {
        toast.error(friendlyApiError(error) || 'Failed to add break');
        setBreakModelVisible(false);
      });
  };

  const handleRemoveBreak = () => {
    const params = {
      break_id: null,
      break: 'Removed',
    };

    postApiCall(`${API_AGENT_BREAK_ADD}/${selectedAgentData?.agent?.id}`, params)
      .then(async (resp: any) => {
        toast.success(`Break Removed Successfully`);
        setAgentPopupVisible(false);
        await fetchRealtimeData();
      })
      .catch((error: any) => {
        toast.error(friendlyApiError(error) || 'Failed to remove break');
      });
  };

  const handleOpenConfirm = () => {
    setShowConfirm(true);
  };

  const handleCloseConfirm = () => {
    setShowConfirm(false);
  };

  const handleConfirmRemoveBreak = () => {
    handleRemoveBreak();
    setShowConfirm(false);
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'agent_name',
      header: 'Agent Name',
      cell: ({ row }: any) => {
        const rowData = row.original;
        const agentName = rowData?.agent?.agent_name || '-';
        const profiles = rowData?.agent?.agentTelephony?.profile || [];
        const currentState = stateOf(rowData);
        const accent =
          currentState === 'oncall'
            ? '#3b82f6'
            : currentState === 'break'
              ? '#f59e0b'
              : currentState === 'online'
                ? '#22c55e'
                : '#94a3b8';
        const initials = String(agentName)
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((p) => p[0])
          .join('')
          .toUpperCase() || 'AG';

        const typeIconFor = (t: string) => {
          const up = String(t || '').toUpperCase();
          if (up.includes('MOBILE')) return <Smartphone className="h-3 w-3" />;
          if (up.includes('WEBRTC')) return <Radio className="h-3 w-3" />;
          if (up.includes('SOFT')) return <Headset className="h-3 w-3" />;
          return <PhoneCall className="h-3 w-3" />;
        };
        return (
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden
              style={{
                width: 3,
                minHeight: 40,
                alignSelf: 'stretch',
                borderRadius: 3,
                background: accent,
                flexShrink: 0,
              }}
            />
            <span
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ring-1 ring-inset"
              style={{
                background: `${accent}1a`,
                color: accent,
                borderColor: `${accent}44`,
              }}
            >
              {initials}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-foreground">
                {agentName}
              </div>
              <div className="mt-1 flex items-center gap-1">
                {['Mobile', 'Soft Phone', 'WEBRTC'].map((type) => {
                  const profile = profiles.find((p: any) => p.type === type);
                  const isActive = !!profile?.active_profile;
                  return (
                    <span
                      key={type}
                      aria-label={
                        !profile
                          ? `${type} not configured`
                          : isActive
                            ? `Currently active: ${type}`
                            : `${type} configured`
                      }
                      title={
                        !profile
                          ? `${type} not configured`
                          : isActive
                            ? `Currently active: ${type}`
                            : `${type} configured`
                      }
                      className={
                        'inline-flex h-6 w-6 items-center justify-center rounded-md ring-1 ring-inset ' +
                        (!profile
                          ? 'bg-muted/40 text-muted-foreground/40 ring-border/40'
                          : isActive
                            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : 'bg-muted/50 text-muted-foreground/70 ring-border/60')
                      }
                    >
                      {typeIconFor(type)}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'call_status',
      header: 'Call Status',
      cell: ({ row }: any) => {
        const rowData = row.original;
        const callStatus = rowData?.agent?.call_status;
        if (callStatus === null || callStatus === undefined) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        const inCall = callStatus == 1;
        return (
          <span
            className={
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              (inCall
                ? 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-950/40 dark:text-blue-300'
                : 'bg-muted/60 text-muted-foreground ring-border/60')
            }
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: inCall ? '#3b82f6' : '#94a3b8',
                animation: inCall
                  ? 'rtLivePulse 1.2s ease-in-out infinite'
                  : 'none',
              }}
            />
            {inCall ? 'In Call' : 'Idle'}
          </span>
        );
      },
    },
    {
      id: 'break_status',
      header: 'Break Status',
      cell: ({ row }: any) => (
        <BreakStatusCapsule
          agent={row.original?.agent}
          user={row.original?.user}
          activeUserBreak={row.original?.activeUserBreak}
          currentTime={currentTime}
        />
      ),
    },
    {
      id: 'login',
      header: 'Log In',
      cell: ({ row }: any) => {
        const loginAt = row.original?.user?.login_at;
        if (!loginAt) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] text-foreground">
            <LogIn className="h-3 w-3 text-emerald-600" />
            {new Date(loginAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        );
      },
    },
    {
      id: 'logout',
      header: 'Log Out',
      cell: ({ row }: any) => {
        const rowData = row.original;
        const canForceLogout =
          (isDashboardLoggedIn(rowData) || isProfileLoggedIn(rowData)) &&
          !!rowData?.agent?.id &&
          isRealtimeRole &&
          realtimeAcl.canEdit;
        if (canForceLogout) {
          const isBusy = logoutBusy && pendingLogout?.agent?.id === rowData?.agent?.id;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPendingLogout(rowData);
              }}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-rose-100 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              title="Force logout"
              aria-label="Force logout"
            >
              {isBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <LogOut className="h-3 w-3" />
              )}
              Log out
            </button>
          );
        }
        if (!rowData?.user?.logout_at)
          return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
            <LogOut className="h-3 w-3 text-rose-600" />
            {new Date(rowData.user.logout_at).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        );
      },
    },
    {
      id: 'extension',
      header: 'Ext / Number',
      cell: ({ row }: any) => {
        const profiles = row.original?.agent?.agentTelephony?.profile;
        const activeProfile = profiles?.find((p: any) => p.active_profile === true);
        const number = activeProfile?.number?.number;
        return number ? (
          <span className="font-mono text-xs text-foreground">{number}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      id: 'queue',
      header: 'Call Group',
      cell: ({ row }: any) => {
        const rowData = row.original;
        const names = rowData.agentGroup?.map((group: any) => group.group_name) || [];
        const grouped = names.reduce((acc: any, name: string, index: number) => {
          const lineIndex = Math.floor(index / 2);
          if (!acc[lineIndex]) acc[lineIndex] = [];
          acc[lineIndex].push(name);
          return acc;
        }, [] as any[]);
        return grouped.length > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAssignedQueueList(...(grouped as any));
              setQueueModel(true);
              setSelectedAgentName(rowData?.agent?.agent_name || '-');
            }}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25 transition-colors hover:bg-primary/15 cursor-pointer"
            title="View assigned call groups"
          >
            <ListChecks className="h-3 w-3" />
            {names.length}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      id: 'dashboard_login',
      header: 'Dashboard Login',
      cell: ({ row }: any) => {
        const status = isDashboardLoggedIn(row.original);
        return (
          <span
            className={
              'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              (status
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-muted/60 text-muted-foreground ring-border/60')
            }
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: status ? '#22c55e' : '#94a3b8',
              }}
            />
            {status ? 'Logged In' : 'Logged Out'}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Profile Login',
      cell: ({ row }: any) => {
        const rowData = row.original;
        const status = isProfileLoggedIn(rowData);
        return (
          <span
            className={
              'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
              (status
                ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-muted/60 text-muted-foreground ring-border/60')
            }
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: status ? '#22c55e' : '#94a3b8',
              }}
            />
            {status ? 'Online' : 'Offline'}
          </span>
        );
      },
    },
    {
      id: 'activity',
      header: 'Activity',
      enableSorting: false,
      cell: ({ row }: any) => {
        const ag = row.original?.agent;
        if (!ag?.id) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActivityAgent(ag);
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
            title="View agent activity"
            aria-label="View agent activity"
          >
            <Clock className="h-3.5 w-3.5" />
          </button>
        );
      },
    },
    // WebRTC trace is superadmin-only, so the column itself is dropped
    // for everyone else rather than rendering an empty header.
    ...(isSuperAdmin
      ? ([{
      id: 'sipTrace',
      header: 'WebRTC',
      enableSorting: false,
      cell: ({ row }: any) => {
        const ag = row.original?.agent;
        if (!ag?.id) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSipTraceAgent(ag);
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
            title="WebRTC trace (sign-in, calls reaching the agent, audio)"
            aria-label="WebRTC trace"
          >
            <Radio className="h-3.5 w-3.5" />
          </button>
        );
      },
    }] as ColumnDef<any>[])
      : []),
  ];

  const queueOptions = queueList?.map((group: any) => ({
    label: group.group_name,
    value: group.id,
  })) || [];

  const assignQueueOptions = assignedQueueList?.map?.((group: any) => ({
    label: group.group_name,
    value: group.id,
  })) || [];

  const secondsAgo = Math.max(0, currentTime - lastUpdatedTs);
  const stalenessLabel =
    secondsAgo < 5
      ? 'just now'
      : secondsAgo < 60
        ? `${secondsAgo}s ago`
        : `${Math.floor(secondsAgo / 60)}m ago`;
  const isStale = secondsAgo > 30;
  const queueWaiting = Number(queueDetail?.count) || 0;
  const queueOverload = queueWaiting > 10;

  const FILTER_CHIPS: Array<{
    key: 'all' | 'oncall' | 'online' | 'break' | 'offline';
    label: string;
    count: number;
    Icon: any;
    activeCls: string;
  }> = [
    {
      key: 'all',
      label: 'All',
      count: realtimeData.length,
      Icon: Users,
      activeCls:
        'bg-primary text-primary-foreground ring-primary/40',
    },
    {
      key: 'oncall',
      label: 'On Call',
      count: stateCounts.oncall,
      Icon: PhoneCall,
      activeCls:
        'bg-blue-500 text-white ring-blue-600',
    },
    {
      key: 'online',
      label: 'Available',
      count: stateCounts.online,
      Icon: Activity,
      activeCls:
        'bg-emerald-500 text-white ring-emerald-600',
    },
    {
      key: 'break',
      label: 'Break',
      count: stateCounts.break,
      Icon: Coffee,
      activeCls: 'bg-amber-500 text-white ring-amber-600',
    },
    {
      key: 'offline',
      label: 'Offline',
      count: stateCounts.offline,
      Icon: PowerOff,
      activeCls:
        'bg-slate-500 text-white ring-slate-600',
    },
  ];

  if (!isRealtimeRole || !realtimeAcl.canRead) {
    return <AclDenied operation="read" />;
  }

  return (
    <FillPage>
      <style>{`
        @keyframes rtLivePulse {
          0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
          70% { transform: scale(1.05); opacity: 0.85; box-shadow: 0 0 0 6px rgba(34,197,94,0); }
          100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(34,197,94,0); }
        }
      `}</style>

      <div className={wallboard ? 'fixed inset-0 z-[9999] overflow-auto bg-background p-6' : ''}>
        {/* Top strip */}
        <div
          className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Radio className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="m-0 text-lg font-bold text-foreground">Real-Time</h4>
              <span
                className={
                  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ' +
                  (isStale
                    ? 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
                    : 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300')
                }
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    backgroundColor: isStale ? '#f59e0b' : '#22c55e',
                    animation: isStale ? 'none' : 'rtLivePulse 1.4s ease-in-out infinite',
                  }}
                />
                {isStale ? 'STALE' : 'LIVE'}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              Live agent + call group snapshot · Updated{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {stalenessLabel}
              </span>
            </div>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              aria-pressed={autoRefresh}
              title={autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
              className={
                'inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold ring-1 ring-inset transition-colors cursor-pointer ' +
                (autoRefresh
                  ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-200'
                  : 'bg-muted/60 text-muted-foreground ring-border/60 hover:bg-muted')
              }
            >
              <RefreshCw
                className={'h-3.5 w-3.5 ' + (autoRefresh ? 'animate-spin' : '')}
                style={{ animationDuration: autoRefresh ? '4s' : undefined }}
              />
              {autoRefresh ? 'Auto' : 'Paused'}
            </button>
            <button
              type="button"
              onClick={() => setWallboard((v) => !v)}
              title={wallboard ? 'Exit wallboard mode' : 'Enter wallboard mode'}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
              aria-label={wallboard ? 'Exit wallboard' : 'Enter wallboard'}
            >
              {wallboard ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        {/* Threshold alert banner */}
        {queueOverload && (
          <div
            className="mb-3 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300"
            role="alert"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{queueWaiting}</strong> calls waiting in call group — consider
              adding agents or bumping the alert level.
            </span>
            <button
              type="button"
              onClick={() => setQueuePopupVisible(true)}
              className="ml-auto rounded-md border border-rose-300 bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-800 transition-colors hover:bg-rose-200 dark:border-rose-800 dark:bg-rose-900/50 dark:text-rose-200 cursor-pointer"
            >
              View call group
            </button>
          </div>
        )}

        {/* KPI row + queue card */}
        <Boxes
          realtimeData={realtimeData}
          setQueuePopupVisible={setQueuePopupVisible}
          queueDetail={queueDetail}
          loggedInUser={loggedInUser}
          stateCounts={stateCounts}
        />

        {/* Agents section header — filter chips + search */}
        <div className="mt-4 mb-3 flex flex-wrap items-center gap-2">
          <div className="mr-auto flex flex-wrap items-center gap-1.5">
            {FILTER_CHIPS.map(({ key, label, count, Icon, activeCls }) => {
              const isActive = stateFilter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStateFilter(key)}
                  className={
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition-colors cursor-pointer ' +
                    (isActive
                      ? activeCls
                      : 'bg-card text-foreground ring-border/60 hover:bg-accent')
                  }
                  aria-pressed={isActive}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                  <span className="ml-1 rounded-full bg-black/10 px-1.5 text-[10px] tabular-nums dark:bg-white/10">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative inline-flex w-full max-w-[280px] items-center">
            <Search className="pointer-events-none absolute left-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or number…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="input-embossed h-9 w-full rounded-md pl-8 pr-8 text-xs"
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                className="absolute right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
                title="Clear"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Agents table */}
        <div
          className="overflow-hidden rounded-xl border border-border/60 bg-card"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <div
            className="scroll-container"
            onClickCapture={(e) => {
              // Row click → open agent popup. TanStack DataTable doesn't
              // expose a top-level onRowClick like PrimeReact does, so we
              // read the clicked <tr>'s row data via a delegated handler.
              const target = e.target as HTMLElement;
              if (
                target.closest('button') ||
                target.closest('a') ||
                target.closest('input')
              ) {
                return;
              }
              const tr = target.closest('tr');
              if (!tr) return;
              const idxAttr = tr.getAttribute('data-row-index');
              if (!idxAttr) return;
              const idx = Number(idxAttr);
              if (Number.isNaN(idx)) return;
              const rowData = filteredData[idx];
              if (rowData) handleRowClick(rowData);
            }}
          >
            <DataTable
              tableId="realtime-agents"
              columns={columns}
              data={filteredData}
              pageSize={10}
              emptyTitle="No agents"
              emptyDescription={
                stateFilter !== 'all'
                  ? `No agents in "${stateFilter}" state right now.`
                  : 'No agents match the current search.'
              }
              disableExport
            />
          </div>
        </div>

        <SipTraceModal
          show={isSuperAdmin && !!sipTraceAgent}
          onHide={() => setSipTraceAgent(null)}
          agentId={sipTraceAgent?.id}
          agentName={sipTraceAgent?.agent_name}
          agentNumber={sipTraceAgent?.agent_number}
        />

        <AgentActivityModal
          show={!!activityAgent}
          onHide={() => setActivityAgent(null)}
          agentId={activityAgent?.id}
          agentName={activityAgent?.agent_name}
          agentNumber={activityAgent?.agent_number}
        />

        <ConfirmDialog
          open={pendingTypeLogin !== null}
          onOpenChange={(o) => {
            if (!o && !typeLoginBusy) setPendingTypeLogin(null);
          }}
          title={
            pendingTypeLogin?.isActive
              ? `Re-login as ${pendingTypeLogin?.type}?`
              : `Switch to ${pendingTypeLogin?.type}?`
          }
          description={
            <>
              Log{' '}
              <span className="font-medium text-foreground">
                {pendingTypeLogin?.agentName}
              </span>{' '}
              in as{' '}
              <span className="font-medium text-foreground">
                {pendingTypeLogin?.type}
              </span>
              ?
            </>
          }
          confirmLabel="Log in"
          loading={typeLoginBusy}
          onConfirm={confirmTypeLogin}
        />

        <ConfirmDialog
          open={pendingLogout !== null}
          onOpenChange={(o) => {
            if (!o && !logoutBusy) setPendingLogout(null);
          }}
          title="Force logout?"
          description={
            <>
              Force logout{' '}
              <span className="font-medium text-foreground">
                {pendingLogout?.agent?.agent_name || 'this agent'}
              </span>
              ? This will end their current session.
            </>
          }
          confirmLabel="Log out"
          tone="destructive"
          loading={logoutBusy}
          onConfirm={confirmDirectLogout}
        />

        <Modal
          show={agentPopupVisible}
          onHide={() => setAgentPopupVisible(false)}
          dialogClassName="w-100"
          centered
        >
          <div
            style={{
              maxWidth: '500px',
              opacity: removeQueueModelView ? 0.1 : 1,
              pointerEvents: removeQueueModelView ? 'none' : 'auto',
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>User Info</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="agent-popup-content">
                <div className="d-flex align-items-center mb-3">
                  <div className="info-icon-list">
                    <span className="popup-info-icon">
                      <i className="icon-user"></i>
                    </span>
                    <span>
                      Role: <b>Agent</b>
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center">
                  <div className="info-icon-list">
                    <span className="popup-info-icon">
                      <i className="icon-user"></i>
                    </span>
                    <span>Extension: </span>
                  </div>
                  <div className="list-info">
                    <b>{extension}</b>
                  </div>
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer className="flex-column align-items-start">
              <div className="button-group w-100">
                <div className="button-row d-flex  mb-2">
                  <Button
                    className="btn btn-primary"
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                    disabled={
                      !selectedAgentData?.agent?.call_status ||
                      selectedAgentData?.agent?.call_status == '0'
                    }
                  >
                    <Headphones size={18} />
                    Listen
                  </Button>
                  <Button
                    className="btn btn-primary"
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                    disabled={
                      !selectedAgentData?.agent?.call_status ||
                      selectedAgentData?.agent?.call_status == '0'
                    }
                  >
                    <MessageCircle size={18} />
                    Whisper
                  </Button>
                  <Button
                    className="btn btn-primary"
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                    disabled={
                      !selectedAgentData?.agent?.call_status ||
                      selectedAgentData?.agent?.call_status == '0'
                    }
                  >
                    <Users size={18} />
                    Conference
                  </Button>
                  <Button
                    className="btn btn-primary"
                    // disabled={selectedAgentData?.agent?.login_status === "0"}
                    // onClick={() => handleLogoutAgent(selectedAgentData)}
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                  >
                    <LogOut size={18} />
                    Logout
                  </Button>
                </div>
                <div className="button-row d-flex  mb-2">
                  {/* <Button
                  className="btn btn-primary"
                  style={{ marginRight: '10px', display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', fontSize: '13px' }}
                  onClick={()=> handleAddBreakClick()}
                  disabled={selectedAgentData?.agent?.break_id !== null}
                >
                  <Pause size={18} />
                  Set Break
                </Button> */}

                  {/* <Button
                  className="btn btn-primary"
                  style={{ marginRight: '10px', display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', fontSize: '13px' }}
                  disabled={selectedAgentData?.agent?.break_id === null}
                  onClick={handleOpenConfirm}
                >
                  <Play size={18} />
                  Remove Break
                </Button> */}

                  <Button
                    className="btn btn-primary"
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                    onClick={() => handleAddQueueClick()}
                  >
                    <UserPlus size={18} />
                    Add to Call Group
                  </Button>
                </div>
                <div className="button-row d-flex ">
                  <Button
                    className="btn btn-primary"
                    style={{
                      marginRight: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      fontSize: '13px',
                    }}
                    onClick={() => handleRemoveQueueClick()}
                  >
                    <UserMinus size={18} />
                    Remove From Call Group
                  </Button>
                </div>
              </div>
            </Modal.Footer>
          </div>
        </Modal>
        {/* Remove from Queue model */}
        <Modal
          show={removeQueueModelView}
          onHide={() => {
            setRemoveQueueModelView(false);
            setSelectedRemoveQueue('');
            setAgentPopupVisible(false);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Remove Call Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Select
              value={
                queueOptions.find((o: any) => o.value === selectedRemoveQueue) || null
              }
              options={queueOptions}
              onChange={(o: any) => setSelectedRemoveQueue(o ? o.value : '')}
              placeholder="Select Call Group"
              className="w-100"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="p-button-secondary"
              style={{ marginRight: '10px' }}
              onClick={() => {
                setRemoveQueueModelView(false);
                setSelectedRemoveQueue('');
                setAgentPopupVisible(false);
              }}
            >
              Close{' '}
            </Button>
            <Button className="p-button-primary" onClick={() => handleRemoveQueue()}>
              Remove
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add In Queue Modal */}
        <Modal
          show={assignQueueModelView}
          onHide={() => {
            setAssignQueueModelView(false);
            setSelectedQueue('');
            setAgentPopupVisible(false);
          }}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Call Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col>
                <label style={{ marginBottom: '10px' }}>Select Call Group:</label>
                <Select
                  value={
                    assignQueueOptions.find((o: any) => o.value === selectedQueue) ||
                    null
                  }
                  options={assignQueueOptions}
                  onChange={(o: any) => setSelectedQueue(o ? o.value : '')}
                  placeholder="Select Call Group"
                  className="w-100"
                  menuPortalTarget={document.body}
                  styles={{
                    container: (base) => ({ ...base, marginBottom: '20px' }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Form>
                  {/* Whole Day Checkbox */}
                  <Form.Check
                    type="checkbox"
                    label="Whole Day"
                    id="wholeDay"
                    className="mb-3"
                    checked={wholeDay}
                    onChange={handleWholeDayChange}
                  />

                  {/* Start Time */}
                  <Form.Group className="mb-3">
                    <Form.Label>Start Time</Form.Label>
                    <CalendarTimeInput
                      value={startTime}
                      onChange={(v) => handleStartTimeChange({ target: { value: v } } as any)}
                      placeholder="00:00"
                      disabled={wholeDay}
                    />
                  </Form.Group>

                  {/* End Time */}
                  <Form.Group className="mb-3">
                    <Form.Label>End Time</Form.Label>
                    <CalendarTimeInput
                      value={endTime}
                      onChange={(v) => handleEndTimeChange({ target: { value: v } } as any)}
                      placeholder="23:59"
                      disabled={wholeDay}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Select Days</Form.Label>
                    <div>
                      {[
                        'Monday',
                        'Tuesday',
                        'Wednesday',
                        'Thursday',
                        'Friday',
                        'Saturday',
                        'Sunday',
                      ].map((day) => (
                        <Form.Check
                          inline
                          key={day}
                          label={day}
                          type="checkbox"
                          checked={memberWeekDays.includes(day)}
                          onChange={(e: any) => {
                            if (e.target.checked) {
                              setmemberWeekDays((prev) => [...prev, day]);
                            } else {
                              setmemberWeekDays((prev) => prev.filter((d) => d !== day));
                            }
                          }}
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Form>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setAssignQueueModelView(false);
                setSelectedQueue('');
                setAgentPopupVisible(false);
              }}
            >
              Close
            </Button>
            <Button variant="primary" onClick={handleAddQueue}>
              Add
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <Modal show={queueModel} onHide={() => setQueueModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '22px' }}>
            Call Groups Allocated to: {selectedAgentName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assignedQueueList.length > 0 ? (
            <div>
              <div style={{ marginBottom: '10px' }}>
                {/* <strong>Assigned Queues:</strong> */}
                <ul style={{ paddingLeft: '16px' }}>
                  {assignedQueueList.map((queue: any, index: number) => (
                    <li key={index}>{queue}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div>No Call Group Allocated.</div>
          )}
        </Modal.Body>
      </Modal>

      {queuePopupVisible && (
        <motion.div
          className="queue-info-popup shadow-lg"
          drag
          dragElastic={0.2}
          dragMomentum={false}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '550px',
            background: isDarkMode ? '#111827' : '#fff',
            borderRadius: '12px',
            boxShadow: isDarkMode
              ? '0 18px 40px rgba(2,6,23,0.45)'
              : '0 4px 20px rgba(0,0,0,0.15)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.18)' : 'none',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
          }}
          onDragEnd={(event: any, info: any) => {
            setPopupPosition((prev) => ({
              x: prev.x + info.offset.x,
              y: prev.y + info.offset.y,
            }));
          }}
        >
          {/* Header */}
          <div
            className="queue-popup-header d-flex justify-content-between align-items-center p-3"
            style={{
              backgroundColor: isDarkMode ? '#0f172a' : 'rgba(8, 50, 105, 1)',
              color: '#fff',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
            }}
          >
            <h5 className="mb-0" style={{ fontWeight: 600 }}>
              Call Group Info
            </h5>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setQueuePopupVisible(false);
              }}
              style={{
                fontSize: '18px',
                textDecoration: 'none',
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              ✖
            </a>
          </div>

          {/* Scrollable Content */}
          <div
            className="queue-popup-content"
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '15px',
              background: isDarkMode ? '#111827' : '#fff',
              color: isDarkMode ? '#e5e7eb' : '#111827',
            }}
          >
            {queueDetail?.queueData
              ?.filter((queue: any) => Object.keys(queue.value).length > 0)
              .map((queue: any, index: number) => {
                const queueName = queue.key.split(':')[3];
                const calls = Object.values(queue.value);

                return (
                  <div key={index} className="mb-4">
                    {/* Queue Name */}
                    <h6
                      className="fw-bold mb-2"
                      style={{
                        color: isDarkMode ? '#93c5fd' : 'rgba(8, 50, 105, 1)',
                      }}
                    >
                      {queueName}
                    </h6>

                    {/* Styled Table */}
                    <div
                      style={{
                        overflow: 'hidden',
                        borderRadius: '8px',
                        border: isDarkMode
                          ? '1px solid rgba(148, 163, 184, 0.18)'
                          : '1px solid #e0e0e0',
                        boxShadow: isDarkMode
                          ? '0 6px 18px rgba(2,6,23,0.22)'
                          : '0 2px 6px rgba(0,0,0,0.05)',
                      }}
                    >
                      <Table
                        bordered
                        size="sm"
                        style={{
                          backgroundColor: isDarkMode ? '#111827' : '#fff',
                          width: '100%',
                          borderCollapse: 'separate',
                          borderSpacing: '0',
                          margin: 0,
                          color: isDarkMode ? '#e5e7eb' : '#111827',
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                backgroundColor: isDarkMode ? '#0f172a' : '#f9f9f9',
                                color: isDarkMode ? '#f8fafc' : '#111827',
                                fontWeight: 600,
                              }}
                            >
                              Customer Number
                            </th>
                            <th
                              style={{
                                backgroundColor: isDarkMode ? '#0f172a' : '#f9f9f9',
                                color: isDarkMode ? '#f8fafc' : '#111827',
                                fontWeight: 600,
                              }}
                            >
                              Call Group Entry Time
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {calls.map((call: any, idx: number) => {
                            const wait = queueWaitSeconds(call.enqueueTime, currentTime);

                            const cellStyle: any = {
                              backgroundColor: isDarkMode
                                ? idx % 2 === 0
                                  ? '#111827'
                                  : '#0f172a'
                                : idx % 2 === 0
                                ? '#f5faff'
                                : '#ffffff',
                              color: isDarkMode ? '#e5e7eb' : '#111827',
                              transition: 'background-color 0.2s ease, color 0.2s ease',
                            };

                            return (
                              <tr
                                key={idx}
                                onMouseEnter={(e: any) => {
                                  e.currentTarget
                                    .querySelectorAll('td')
                                    .forEach((td: any) => {
                                      td.style.backgroundColor = isDarkMode
                                        ? '#172554'
                                        : '#d9edf7';
                                      td.style.color = isDarkMode ? '#f8fafc' : '#111827';
                                    });
                                }}
                                onMouseLeave={(e: any) => {
                                  e.currentTarget
                                    .querySelectorAll('td')
                                    .forEach((td: any) => {
                                      td.style.backgroundColor = isDarkMode
                                        ? idx % 2 === 0
                                          ? '#111827'
                                          : '#0f172a'
                                        : idx % 2 === 0
                                        ? '#f5faff'
                                        : '#ffffff';
                                      td.style.color = isDarkMode ? '#e5e7eb' : '#111827';
                                    });
                                }}
                              >
                                <td style={cellStyle}>{call.calleeTo}</td>
                                <td style={cellStyle}>{wait == null ? '-' : formatDuration(wait)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Footer */}
          <div
            className="queue-popup-footer d-flex justify-content-end p-3 border-top"
            style={{
              backgroundColor: isDarkMode ? '#0f172a' : '#f9f9f9',
              borderTopColor: isDarkMode ? 'rgba(148, 163, 184, 0.18)' : undefined,
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
            }}
          >
            <button
              style={{
                backgroundColor: 'rgba(8, 50, 105, 1)',
                color: '#fff',
                border: 'none',
                padding: '10px 18px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.3s ease',
              }}
              onMouseEnter={(e: any) =>
                (e.currentTarget.style.backgroundColor = 'rgba(8, 50, 105, 1)')
              }
              onMouseLeave={(e: any) =>
                (e.currentTarget.style.backgroundColor = 'rgba(8, 50, 105, 1)')
              }
              onClick={() => setQueuePopupVisible(false)}
            >
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Break*/}
      <Modal
        show={breakModelVisible}
        onHide={() => {
          setBreakModelVisible(false);
          setSelectedBreak('');
          setAgentPopupVisible(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Select
            value={breakList.find((o: any) => o.value === selectedBreak) || null}
            options={breakList}
            onChange={(o: any) => setSelectedBreak(o ? o.value : '')}
            placeholder="Select Break"
            className="w-100"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="p-button-secondary"
            style={{ marginRight: '10px' }}
            onClick={() => {
              setBreakModelVisible(false);
              setSelectedBreak('');
              setAgentPopupVisible(false);
            }}
          >
            Close{' '}
          </Button>
          <Button className="p-button-primary" onClick={() => handleAddBreak()}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirm} onHide={handleCloseConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to remove break?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseConfirm}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemoveBreak}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
};

export default RealtimeList;
